import {Button, Paper, TextField} from '@material-ui/core';
import {ANALYTICS_EVENT_ACTION} from 'Core/components/Analytics/AnalyticsEventConstants';
import {changeAnalyticsState} from 'Core/ducks/analytics';
import {isLoaded, Loaded} from 'Core/models/Async';
import {getPasswordRequirements, ValidPassword} from 'Core/models/Password';
import {isRight} from 'fp-ts/lib/Either';
import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {showRequirementErrors} from 'Shared/components/ErrorPopover';
import Loading from 'Shared/components/Loading';
import ValidatableTextField from 'Shared/components/ValidatableTextField';
import {SecurityQuestion} from 'Users/api/api';
import 'Users/components/UserRecovery/PasswordReset/PasswordReset.sass';
import {
  changePassword,
  updatePasswordByCurrent,
  changeUpdatePasswordError,
  updateCurrentPassword,
  resetTemporaryPassword,
} from 'Users/ducks/userProfile';
import {UserSecurityQuestions} from './UserSecurityQuestionsSection';

const passwordInputProps = {
  type: 'password',
};

export type PasswordResetSectionProps = {
  onReset?: () => void;
  resetModalCheck?: boolean;
};

const PasswordResetSection = ({onReset, resetModalCheck}: PasswordResetSectionProps) => {
  const dispatch = useDispatch();
  const [showPasswordErrors, setShowPasswordErrors] = useState(false);
  const {pwValue: password, userClaims, passwordUpdateError, currentPassword} = useSelector(
    (state: ReducerState) => state.user.profile
  );
  const {securityQuestionForm} = useSelector((state: ReducerState) => state.user.userAccount);
  const {analytics} = useSelector((state: ReducerState) => state.analytics);

  useEffect(() => {
    return () => {
      dispatch(changeUpdatePasswordError());
    };
  }, [dispatch, changeUpdatePasswordError]);

  const mapQuestions = (questions: UserSecurityQuestions): SecurityQuestion[] => [
    {
      id: questions.securityQuestionOne.question,
      response: questions.securityQuestionOne.answer,
    },
    {
      id: questions.securityQuestionTwo.question,
      response: questions.securityQuestionTwo.answer,
    },
  ];

  return isLoaded(userClaims) && isLoaded(securityQuestionForm) && isLoaded(password) ? (
    <Paper className="user-profile__user-info-section">
      <div className="user-info-section__header">
        <h3>Update Password</h3>
      </div>
      {passwordUpdateError && <div className="user-profile__error-message">{passwordUpdateError}</div>}
      <TextField
        id="current-password-input"
        className="password-reset__current-password-input"
        label="Current Password"
        onChange={e => {
          dispatch(updateCurrentPassword(e.target.value));
        }}
        variant="outlined"
        value={currentPassword}
        inputProps={passwordInputProps}
      />
      <ValidatableTextField
        id="new-password-input"
        className="password-reset__new-password-input"
        label="New Password"
        showErrors={showRequirementErrors(showPasswordErrors)}
        errors={getPasswordRequirements(password.Data)}
        onChange={passwordLine1 =>
          dispatch(changePassword(new Loaded(ValidPassword.create(passwordLine1, password.Data.passwordLine2))))
        }
        value={password.Data.passwordLine1}
        inputProps={passwordInputProps}
      />
      <ValidatableTextField
        id="confirm-password-input"
        className="password-reset__confirm-password-input"
        label="Confirm Password"
        onBlur={() => setShowPasswordErrors(true)}
        showErrors={showRequirementErrors(showPasswordErrors)}
        errors={getPasswordRequirements(password.Data)}
        onChange={passwordLine2 =>
          dispatch(changePassword(new Loaded(ValidPassword.create(password.Data.passwordLine1, passwordLine2))))
        }
        value={password.Data.passwordLine2}
        inputProps={passwordInputProps}
      />
      <div className="password-reset__actions">
        <Button
          id="password-reset-submit-button"
          onClick={() => {
            dispatch(
              changeAnalyticsState({
                page: analytics.page,
                ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.USER_PROFILE_UPDATEPASSWORD},
              })
            );
            if (password.Data instanceof ValidPassword) {
              if (resetModalCheck) {
                dispatch(
                  resetTemporaryPassword(
                    userClaims.Data.id,
                    currentPassword,
                    password.Data.passwordLine1,
                    {
                      password: password.Data.passwordLine1,
                      securityQuestions: isRight(securityQuestionForm.Data)
                        ? mapQuestions(securityQuestionForm.Data.right)
                        : mapQuestions(securityQuestionForm.Data.left),
                    },
                    onReset
                  )
                );
              } else {
                dispatch(
                  updatePasswordByCurrent(userClaims.Data.id, currentPassword, password.Data.passwordLine1, onReset)
                );
              }
            }
          }}
          type="submit"
          variant="contained"
          color="primary"
          disabled={!(password.Data instanceof ValidPassword)}
        >
          Update Password
        </Button>
      </div>
    </Paper>
  ) : (
    <Paper className="user-profile__user-info-section">
      <Loading />
    </Paper>
  );
};

export default PasswordResetSection;
